import React from 'react';
import BaseSelect, { SelectChangeEvent } from '@mui/material/Select';
import { Box, Checkbox, FormHelperText, MenuItem, SxProps, styled } from '@mui/material';
import { Typography } from '../Typography';
import BkToolTip from 'src/components/tooltip/BkToolTip';

interface BkSelectProps {
  label?: string;
  data: string[] | undefined | { id: string; label: string }[];
  onChange: (event: SelectChangeEvent<any>) => void;
  value: string | string[] | undefined;
  error: string | undefined;
  disabled: boolean;
  placeholder?: string;
  width?: string;
  hasLabel?: boolean;
  name?: string;
  hideHelperText?: boolean;
  multiple?: boolean;
  tooltip?: React.ReactNode;
  required?: boolean;
  autoComplete?: string;
  containerSx?: SxProps;
  /**
   * Optional custom renderer for the content of each dropdown option. When
   * omitted, the option's plain `label` text is rendered. The closed-state
   * display always uses `label`, so this only affects the open menu.
   */
  renderOptionLabel?: (item: { id: string; label: string }) => React.ReactNode;
}

function isDataArrayOfObjects(arr: any): arr is { id: string; label: string }[] {
  return (
    Array.isArray(arr) &&
    arr.every(
      (item) =>
        typeof item === 'object' && item !== null && (typeof item.id === 'string' || typeof item.id === 'number') && typeof item.label === 'string',
    )
  );
}

//default values so that the select options always open under the select bar
const ITEM_HEIGHT = 48;

const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: start;
  max-width: 100%;
  gap: 0.25rem;
`;

const Select: React.FC<BkSelectProps> = ({
  label,
  data,
  onChange,
  value,
  error,
  disabled,
  placeholder,
  width,
  name,
  hideHelperText,
  multiple,
  required,
  autoComplete,
  tooltip,
  containerSx = {},
  renderOptionLabel,
}) => {
  return (
    <Container sx={containerSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {label && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              fontFamily: 'Instrument Sans',
              lineHeight: '1.25rem',
              color: error ? '#AC3737' : '#45464F',
            }}
          >
            {label}
            {required ? '*' : null}
          </Typography>
        )}
        {tooltip}
      </Box>

      <BaseSelect
        labelId={`bkselect-${label}`}
        notched={true}
        value={value}
        autoComplete={autoComplete}
        error={error !== '' && error !== undefined}
        sx={{
          width: width || '100%',
          '& .MuiSelect-select span::before': {
            content: "'Select...'",
            color: '#7E828B',
          },
          '& .MuiInputBase-input': {
            borderRadius: '0.375rem',
            border: error ? '0.0625rem solid #AC3737' : '0.0625rem solid #7E828B',
            fontSize: '1rem',
            fontWeight: 400,
            fontFamily: 'Instrument Sans',
            lineHeight: '1.5rem',
            padding: '0.75rem 1rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        MenuProps={MenuProps}
        name={name}
        onChange={onChange}
        multiple={multiple}
        disabled={disabled}
        placeholder={placeholder}
        displayEmpty
        renderValue={(value: any) => {
          if (!value || value.length === 0) {
            return placeholder;
          }
          if (!isDataArrayOfObjects(data)) {
            return multiple ? value.join(', ') : value;
          }
          const getLabel = (id: string) => data.find((item) => item.id === id)?.label || id;
          return multiple ? value.map(getLabel).join(', ') : getLabel(value);
        }}
      >
        {data !== undefined ? (
          data.map((item) => {
            if (typeof item === 'string') {
              return (
                <MenuItem key={`select_option_${item}`} value={item}>
                  {item}
                </MenuItem>
              );
            } else if (item !== undefined && item !== null) {
              return (
                <MenuItem key={`select_option_${item.label}`} value={item.id}>
                  {multiple && <Checkbox checked={value?.includes(item.id)} />}
                  {renderOptionLabel ? renderOptionLabel(item) : item.label}
                </MenuItem>
              );
            }
          })
        ) : (
          <></>
        )}
      </BaseSelect>
      {!hideHelperText && error !== '' && error !== undefined && (
        <FormHelperText
          sx={{
            color: '#AC3737',
            marginTop: 0,
            fontFamily: 'Instrument Sans',
            fontSize: '0.875rem',
            lineHeight: '1rem',
            height: '1.5rem',
          }}
        >
          {error}
        </FormHelperText>
      )}
    </Container>
  );
};

export default Select;
